import { useCallback } from 'react';

import useUrlParams from './useUrlParams';

/**
 * The `?q=` KQL query of a list page (cards, collections), kept in the URL so
 * refreshes, back/forward, and shared links keep the filter — the same idiom
 * as `useItemsViewParams` on the items page, which owns more params.
 *
 * `query` is the trimmed text ('' when absent). `setQuery(text)` writes it
 * back with `replace: true` (a filter edit is not a history entry); blank or
 * undefined deletes the param outright so an unfiltered URL stays canonical.
 * Other params in the URL are left alone.
 *
 * `setQuery` keeps ONE identity for the page's lifetime. react-router 6's
 * `setSearchParams` is a new function on every query-string change, and a
 * list's fetch effect depends on its query-error handler, which depends on
 * `setQuery`: built directly on it, any change to the address refetched the
 * list. The identity is hooks/useUrlParams' (KPR-1579), which this wraps.
 */
const SPEC = { q: { default: '' } };

export function useListQuery() {
	// URLSearchParams decodes for us, so this is plain KQL text by the time
	// the builder and the API client see it.
	const { params, update } = useUrlParams(SPEC);

	const setQuery = useCallback(text => {
		update({ q: text === undefined || text === null ? '' : `${text}`.trim() });
	}, [update]);

	return { query: params.q, setQuery };
}

export default useListQuery;
